import { createApiRef, type DiscoveryApi, type FetchApi } from '@backstage/core-plugin-api';
import type {
  CopilotAiCreditUsageResponse,
  CopilotBudgetAccess,
  CopilotBudgetResponse,
  DirectReportsResponse,
} from '../types';

export interface CopilotBudgetApi {
  getAccess(aaid?: string): Promise<CopilotBudgetAccess>;
  getDirectReports(aaid: string, actingAaid?: string): Promise<DirectReportsResponse>;
  getCurrentBudget(aaid: string): Promise<CopilotBudgetResponse>;
  getAiCreditUsage(aaid: string): Promise<CopilotAiCreditUsageResponse>;
  setBudget(employeeId: string, budgetAmount: number): Promise<void>;
}

export const copilotBudgetApiRef = createApiRef<CopilotBudgetApi>({
  id: 'plugin.copilot-budget.service',
});

export class CopilotBudgetApiClient implements CopilotBudgetApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getDirectReports(aaid: string, actingAaid?: string): Promise<DirectReportsResponse> {
    const actingQuery = actingAaid ? `&as=${encodeURIComponent(actingAaid)}` : '';
    return this.get<DirectReportsResponse>('/copilot-budget/direct-reports', aaid, 'direct reports', actingQuery);
  }

  async getAccess(aaid?: string): Promise<CopilotBudgetAccess> {
    return this.get<CopilotBudgetAccess>('/copilot-budget/access', aaid, 'Copilot budget access');
  }

  async getCurrentBudget(aaid: string): Promise<CopilotBudgetResponse> {
    return this.get<CopilotBudgetResponse>('/copilot-budget/current', aaid, 'Copilot budget');
  }

  async getAiCreditUsage(aaid: string): Promise<CopilotAiCreditUsageResponse> {
    return this.get<CopilotAiCreditUsageResponse>('/copilot-budget/ai-credit-usage', aaid, 'Copilot AI credit usage');
  }

  async setBudget(employeeId: string, budgetAmount: number): Promise<void> {
    const response = await this.fetchApi.fetch(`${await this.baseUrl()}/copilot/budget`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employeeId, budgetAmount }),
    });

    if (!response.ok) {
      throw new Error(`Unable to update Copilot budget (${response.status})`);
    }
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('github');
  }

  private async get<T>(path: string, aaid: string | undefined, subject: string, extraQuery = ''): Promise<T> {
    const query = aaid === undefined ? '' : `?aaid=${encodeURIComponent(aaid)}${extraQuery}`;
    const url = `${await this.baseUrl()}${path}${query}`;
    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw new Error(`Unable to load ${subject} (${response.status})`);
    }

    return response.json() as Promise<T>;
  }
}
